export const DEFECT_BOARD_COLS = [
  "Open",
  "In Progress",
  "Needs Discussion",
  "Re-opened",
  "Fixed",
  "To Be Tested",
  "Closed",
  "Rejected",
];

// Valid next statuses for each current status (enforced in UI and backend)
export const ALLOWED_TRANSITIONS = {
  "Open":             ["In Progress", "Rejected", "Needs Discussion"],
  "In Progress":      ["Fixed", "Rejected", "Needs Discussion"],
  "Fixed":            ["To Be Tested", "Rejected", "Needs Discussion"],
  "To Be Tested":     ["Closed", "Re-opened", "Rejected", "Needs Discussion"],
  "Re-opened":        ["In Progress", "Rejected", "Needs Discussion"],
  "Needs Discussion": ["Open", "In Progress", "Fixed", "To Be Tested", "Rejected"],
  "Rejected":         ["Open"],
  "Closed":           ["Re-opened"],
};

export const DEFECT_FILTER_FIELDS = [
  { key: "id", label: "ID", type: "text" },
  { key: "title", label: "Title", type: "text" },
  { key: "severity", label: "Severity", type: "choice", staticOptions: ["Blocker", "Critical", "High", "Medium", "Low"] },
  { key: "priority", label: "Priority", type: "choice", staticOptions: ["Critical", "High", "Medium", "Low"] },
  { key: "defect_type", label: "Type", type: "choice" },
  { key: "status", label: "Status", type: "choice", staticOptions: ["Open", "In Progress", "Fixed", "To Be Tested", "Re-opened", "Closed", "Rejected", "Needs Discussion"] },
  { key: "environment", label: "Environment", type: "choice", staticOptions: ["Dev", "QA", "UAT", "Staging", "Production"] },
  { key: "visibility",  label: "Visibility",  type: "choice", staticOptions: ["Public", "Internal", "Private"] },
  { key: "source", label: "Source", type: "choice", staticOptions: ["Web", "Mobile", "API", "Infra", "Others"] },
  { key: "reporting_channel", label: "Reporting Channel", type: "choice", staticOptions: ["Email", "Call", "Client Portal", "WhatsApp", "Telegram", "Internal QA", "External QA", "Monitoring", "Meeting"] },
  { key: "reported_by_name", label: "Reporter", type: "choice" },
  { key: "assigned_to_name", label: "Owner", type: "choice" },
  { key: "reported_team_id", label: "Reported Team", type: "choice" },
  { key: "owning_team_id", label: "Owning Team", type: "choice" },
  { key: "project_name", label: "Project", type: "choice" },
  { key: "customer_name", label: "Customer", type: "choice" },
  { key: "aging", label: "Aging (days)", type: "text" },
  { key: "created_at", label: "Created", type: "daterange" },
  { key: "updated_at", label: "Updated", type: "daterange" },
  { key: "due_date", label: "Due", type: "daterange" },
  { key: "closed_at", label: "Closed", type: "daterange" },
  // Matches created_at OR closed_at in range (activity_from/activity_to
  // server-side — see GET /defects's docstring in routers/defects.py) —
  // deliberately a separate field from "Created" above, not just a
  // relabeling of it: editing "Created" must never accidentally widen to
  // also match closed_at. Normally set implicitly by a Dashboard Lifecycle-
  // bar segment click (handleLcSelect, DefectsViewV2Page.jsx), but exposed
  // here as a real, manually-pickable field too so it's visible in the
  // Advanced Filter Popover instead of silently counted-but-invisible —
  // do not remove without also reverting countActiveFilters back to
  // needing an exclusion for it.
  { key: "activity_window", label: "Created / Closed", type: "daterange" },
  { key: "tags", label: "Tags", type: "choice" },
];

export const DEFAULT_DEFECT_APPLIED_FILTERS = {
  status: {
    op: "is_not",
    values: ["Closed", "Rejected"],
  },
};

const SEV_DONUT_TEMPLATE = [
  {
    k: "Critical", lb: "Critical", c: "#B84A52",
    projects: [
      { k: "trd", lb: "Trading", c: "#B84A52" },
      { k: "rsr", lb: "Research", c: "#C86A6A" },
      { k: "rnc", lb: "Risk", c: "#D88A8A" },
      { k: "bo", lb: "Back Office", c: "#E8AAAA" },
      { k: "rm", lb: "Mobile", c: "#F3C5C5" },
    ]
  },
  {
    k: "Blocker", lb: "Blocker", c: "#C05A62",
    projects: [
      { k: "trd", lb: "Trading", c: "#C05A62" },
      { k: "rsr", lb: "Research", c: "#CE7878" },
      { k: "rnc", lb: "Risk", c: "#DC9595" },
      { k: "bo", lb: "Back Office", c: "#EBB3B3" },
      { k: "rm", lb: "Mobile", c: "#F5CECE" },
    ]
  },
  {
    k: "High", lb: "High", c: "#C87B3A",
    projects: [
      { k: "trd", lb: "Trading", c: "#C87B3A" },
      { k: "rsr", lb: "Research", c: "#D89255" },
      { k: "rnc", lb: "Risk", c: "#E5A870" },
      { k: "bo", lb: "Back Office", c: "#EFBE93" },
      { k: "rm", lb: "Mobile", c: "#F6D4B5" },
    ]
  },
  {
    k: "Medium", lb: "Medium", c: "#5A8F6E",
    projects: [
      { k: "trd", lb: "Trading", c: "#5A8F6E" },
      { k: "rsr", lb: "Research", c: "#74A585" },
      { k: "rnc", lb: "Risk", c: "#8EBD9C" },
      { k: "bo", lb: "Back Office", c: "#A9D2B5" },
      { k: "rm", lb: "Mobile", c: "#C5E3CC" },
    ]
  },
  {
    k: "Low", lb: "Low", c: "#4A78B0",
    projects: [
      { k: "trd", lb: "Trading", c: "#4A78B0" },
      { k: "rsr", lb: "Research", c: "#6590C0" },
      { k: "rnc", lb: "Risk", c: "#80A9D0" },
      { k: "bo", lb: "Back Office", c: "#9EC0DF" },
      { k: "rm", lb: "Mobile", c: "#BDD5EC" },
    ]
  },
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatDateShort(value) {
  if (!value) return "";
  const dt = value instanceof Date ? value : new Date(value);
  return `${String(dt.getDate()).padStart(2, "0")} ${MONTHS[dt.getMonth()]}`;
}

export function ageDays(createdAt) {
  if (!createdAt) return 0;
  const diff = Date.now() - new Date(createdAt).getTime();
  return Math.max(0, Math.floor(diff / 86400000));
}

export function defectId(defect) {
  return defect?.defect_code || defect?.id || "";
}

export function isSlaBreach(defect) {
  if (!defect?.due_date) return false;
  if (defect?.status === "Closed") return false;
  return new Date(defect.due_date) < new Date();
}

export function toDateKey(value) {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

export function getDefectFieldValue(defect, key) {
  switch (key) {
    case "id":
      return defectId(defect);
    case "title":
      return defect?.title || "";
    case "severity":
      return defect?.severity || "";
    case "priority":
      return defect?.priority || "";
    case "defect_type":
      return defect?.defect_type || "";
    case "status":
      return defect?.status || "";
    case "environment":
      return defect?.environment || "";
    case "reported_by_name":
      return defect?.reported_by_name || "";
    case "assigned_to_name":
      return defect?.assigned_to_name || "";
    case "project_name":
      return defect?.project_name || "";
    case "customer_name":
      return defect?.customer_name || "";
    case "aging":
      return String(ageDays(defect?.created_at));
    case "created_at":
      return toDateKey(defect?.created_at);
    case "updated_at":
      return toDateKey(defect?.updated_at);
    case "due_date":
      return toDateKey(defect?.due_date);
    case "closed_at":
      return toDateKey(defect?.closed_at);
    default:
      return "";
  }
}

export function hasActiveAdvancedFilter(filterDef) {
  if (!filterDef) return false;
  if (filterDef.type === "text") return !!filterDef.value?.trim();
  if (filterDef.type === "daterange") return !!(filterDef.from || filterDef.to);
  return Array.isArray(filterDef.values) && filterDef.values.length > 0;
}

export function buildComboData({ defects, range, customRange }) {
  const safeDefects = Array.isArray(defects) ? defects : [];
  const timestamps = [];

  if (range === "custom" && customRange?.from && customRange?.to) {
    const fromMs = new Date(customRange.from).getTime();
    const toMs = new Date(customRange.to).getTime();
    for (let ms = fromMs; ms <= toMs; ms += 86400000) timestamps.push(ms);
  } else {
    const rangeDays = parseInt(range, 10);
    const now = Date.now();
    for (let i = rangeDays - 1; i >= 0; i--) timestamps.push(now - i * 86400000);
  }

  const data = timestamps.map((ts) => {
    const dateStr = new Date(ts).toISOString().slice(0, 10);
    const opened = safeDefects.filter((d) => (d.created_at || "").slice(0, 10) === dateStr).length;
    const closed = safeDefects.filter((d) => d.status === "Closed" && (d.closed_at || "").slice(0, 10) === dateStr).length;
    return { label: formatDateShort(new Date(ts)), opened, closed, cumOpened: 0, cumClosed: 0 };
  });

  let cumOpened = 0;
  let cumClosed = 0;
  data.forEach((row) => {
    cumOpened += row.opened;
    cumClosed += row.closed;
    row.cumOpened = cumOpened;
    row.cumClosed = cumClosed;
  });

  return data;
}

export function buildRangeStats(defects) {
  const safeDefects = Array.isArray(defects) ? defects : [];
  const openDefects = safeDefects.filter((d) => d.status === "Open");
  const open = openDefects.length;
  const critOpen = safeDefects.filter((d) => ["Open", "In Progress", "Re-opened"].includes(d.status) && ["Critical", "Blocker"].includes(d.severity)).length;
  const slaBreach = safeDefects.filter((d) => isSlaBreach(d)).length;
  const total = safeDefects.length;
  const slaPct = total > 0 ? (slaBreach / total) * 100 : 0;
  const openAges = openDefects.map((d) => ageDays(d.created_at));
  const avgAge = openAges.length > 0 ? openAges.reduce((a, b) => a + b, 0) / openAges.length : 0;

  const openedN = total;
  const closedN = safeDefects.filter((d) => d.status === "Closed").length;
  const fixRate = openedN > 0 ? closedN / openedN : null;

  const statusCounts = {};
  safeDefects.forEach((d) => {
    statusCounts[d.status] = (statusCounts[d.status] || 0) + 1;
  });

  const agingBuckets = {
    b0: openDefects.filter((d) => ageDays(d.created_at) <= 7).length,
    b1: openDefects.filter((d) => {
      const age = ageDays(d.created_at);
      return age >= 8 && age <= 14;
    }).length,
    b2: openDefects.filter((d) => {
      const age = ageDays(d.created_at);
      return age >= 15 && age <= 30;
    }).length,
    b3: openDefects.filter((d) => ageDays(d.created_at) > 30).length,
  };

  const moduleMap = {};
  safeDefects
    .filter((d) => d.status !== "Closed" && d.status !== "Rejected")
    .forEach((d) => {
      const key = d.project_name || "Unknown";
      if (!moduleMap[key]) {
        moduleMap[key] = {
          name: key,
          code: d.customer_name || "",
          n: 0,
          hasCrit: false,
          hasHigh: false,
        };
      }
      moduleMap[key].n += 1;
      if (["Critical", "Blocker"].includes(d.severity)) moduleMap[key].hasCrit = true;
      if (d.severity === "High") moduleMap[key].hasHigh = true;
    });
  const topModules = Object.values(moduleMap).sort((a, b) => b.n - a.n);

  const sevCountMap = {};
  const projSevMap = {};
  safeDefects.forEach((d) => {
    const severity = d.severity || "Medium";
    const project = d.project_name || "Unknown";
    sevCountMap[severity] = (sevCountMap[severity] || 0) + 1;
    if (!projSevMap[severity]) projSevMap[severity] = {};
    projSevMap[severity][project] = (projSevMap[severity][project] || 0) + 1;
  });

  const sevData = SEV_DONUT_TEMPLATE
    .map((tier) => {
      const sevN = sevCountMap[tier.k] || 0;
      const projData = projSevMap[tier.k] || {};
      const projects = Object.entries(projData)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name, n], idx) => ({
          k: name.toLowerCase().replace(/\s+/g, "-"),
          lb: name.length > 14 ? `${name.slice(0, 12)}…` : name,
          n,
          c: tier.projects[idx]?.c || tier.c,
        }));
      return { ...tier, n: sevN, projects };
    })
    .filter((tier) => tier.n > 0);

  return {
    open,
    critOpen,
    slaBreach,
    slaPct,
    avgAge,
    fixRate,
    statusCounts,
    agingBuckets,
    openCount: open,
    topModules,
    sevData,
    total,
  };
}

export function buildFilteredDefects({ defects, lcSelected, search, appliedFilters, filterMode }) {
  let list = Array.isArray(defects) ? defects : [];
  const serverOnlyFilterKeys = new Set([
    "reported_team_id", "owning_team_id",
    "status",
    "severity", "priority", "environment", "defect_type",
    "created_at", "updated_at", "due_date", "closed_at",
    // Set only by handleLcSelect (DefectsViewV2Page.jsx) when arriving via a
    // Lifecycle-bar segment click — created OR closed in range, matched
    // server-side only (GET /defects's activity_from/activity_to); has no
    // getDefectFieldValue case, so it must stay excluded from client-side
    // re-filtering the same way the other server-only date fields are.
    "activity_window",
    "reported_by_name", "assigned_to_name",
    "customer_name", "project_name",
    "visibility",
    "source", "reporting_channel",
    "tags",
  ]);

  if (lcSelected) list = list.filter((d) => d.status === lcSelected);

  const searchText = (search || "").trim().toLowerCase();
  if (searchText) {
    list = list.filter((d) =>
      DEFECT_FILTER_FIELDS.some((field) =>
        String(getDefectFieldValue(d, field.key) || "").toLowerCase().includes(searchText)
      )
    );
  }

  const filterEntries = Object.entries(appliedFilters || {})
    .filter(([field, filterDef]) => !serverOnlyFilterKeys.has(field) && hasActiveAdvancedFilter(filterDef));
  if (filterEntries.length > 0) {
    list = list.filter((d) => {
      const checks = filterEntries.map(([field, filterDef]) => {
        const val = String(getDefectFieldValue(d, field) || "");
        if (filterDef.type === "text") {
          return val.toLowerCase().includes(String(filterDef.value || "").toLowerCase());
        }
        if (filterDef.type === "daterange") {
          if (!val) return false;
          if (filterDef.from && val < filterDef.from) return false;
          if (filterDef.to && val > filterDef.to) return false;
          return true;
        }
        const values = Array.isArray(filterDef.values) ? filterDef.values : [];
        if (values.length === 0) return true;
        const match = values.includes(val);
        return filterDef.op === "is_not" ? !match : match;
      });
      return filterMode === "all" ? checks.every(Boolean) : checks.some(Boolean);
    });
  }

  return list;
}

export function buildMyDefects(defects, user, viewTab) {
  if (viewTab === "mine") return defects;
  const me = user?.name || user?.preferred_username || user?.email || "";
  if (!me) return defects;
  const marker = me.split("@")[0].toLowerCase();
  return defects.filter((d) => (d.assigned_to_name || "").toLowerCase().includes(marker));
}

export function buildBoardColumns(defects) {
  const extras = [...new Set((defects || []).map((d) => d.status).filter(Boolean))]
    .filter((status) => !DEFECT_BOARD_COLS.includes(status))
    .sort((a, b) => a.localeCompare(b));
  return [...DEFECT_BOARD_COLS, ...extras];
}

export function buildActiveFilters({ lcSelected, search, appliedFilters }) {
  const active = [];
  if (lcSelected) active.push(lcSelected);
  if (search) active.push(`"${search}"`);

  Object.entries(appliedFilters || {}).forEach(([key, filterDef]) => {
    if (!hasActiveAdvancedFilter(filterDef)) return;
    const field = DEFECT_FILTER_FIELDS.find((entry) => entry.key === key);
    const label = field?.label || key;

    if (filterDef.type === "text") {
      active.push(`${label}: "${filterDef.value}"`);
    } else if (filterDef.type === "daterange") {
      active.push(`${label}: ${filterDef.from || "…"} → ${filterDef.to || "…"}`);
    } else {
      active.push(`${label}: ${(filterDef.values || []).join(", ")}`);
    }
  });

  return active;
}

export function countActiveFilters(appliedFilters) {
  return Object.values(appliedFilters || {}).filter((filterDef) => hasActiveAdvancedFilter(filterDef)).length;
}
